import React, { FC } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
// Components
import {
  FormControlLabel,
  Checkbox,
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Divider,
  Tooltip
} from '@mui/material';
import { ExpandMore, Edit, FavoriteBorder, Favorite, AssignmentLate } from '@mui/icons-material';
// Types
import { IOrderDetail } from '../../../models';
import { formatMoney, formatDate, UNDEFINED_PART } from '../../../helpers';

interface IMobileOrderManagement {
  original: IOrderDetail;
  handleEdit: (val: IOrderDetail) => void;
  handleFavorite: (val: IOrderDetail) => void;
  selectedOrders: IOrderDetail[];
  setSelectedOrders: (val: IOrderDetail[]) => void;
  isActive: boolean;
}

export const MobileOrderManagement: FC<IMobileOrderManagement> = ({
  original,
  handleEdit,
  handleFavorite,
  selectedOrders,
  setSelectedOrders,
  isActive
}) => {
  const classes = useStyles();
  const {
    parts,
    fieldOrderId,
    type,
    job,
    vendors,
    sageOrderNumbers,
    orderPlacer,
    totalCost,
    currentStatus,
    description,
    dateOrdered
  } = original;
  const ids = selectedOrders.map(order => order.fieldOrderId);
  const isSelected = ids.includes(original.fieldOrderId);
  const showCheckbox = original.canApproveOrder;
  const showWarning = original.potentialDuplicateOrderIds.length > 0;

  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          {showWarning ? (
            <Tooltip className={classes.duplicateIcon} title="This order could be a duplicate">
              <AssignmentLate />
            </Tooltip>
          ) : (
            showCheckbox && (
              <FormControlLabel
                classes={{
                  root: classes.tableCheckbox
                }}
                color="primary"
                control={
                  <Checkbox
                    checked={isSelected}
                    id="orders-checkbox"
                    onClick={e => {
                      e.stopPropagation();
                      isSelected
                        ? setSelectedOrders(
                            selectedOrders.filter(
                              selectedOrder => selectedOrder.fieldOrderId !== original.fieldOrderId
                            )
                          )
                        : setSelectedOrders([...selectedOrders, original]);
                    }}
                  />
                }
                label=""
              />
            )
          )}
          <Typography className={classes.activeName} variant="body2">
            Blinq No: {fieldOrderId}
          </Typography>
          <Divider orientation="vertical" flexItem className={classes.divider} />
          <Typography className={classes.part} variant="body2">
            {parts &&
              parts.length > 0 &&
              parts.map((part, index) => {
                return (
                  <span
                    key={`${index}`}
                    className={
                      part.partName === UNDEFINED_PART ? classes.undefinedPart : classes.partItem
                    }
                  >
                    {part.partName}
                    {index !== parts.length - 1 ? ', ' : ''}
                  </span>
                );
              })}
          </Typography>
          <div className={classes.buttonsWrapper}>
            <IconButton
              className={classes.editButton}
              color="secondary"
              onClick={e => {
                e.stopPropagation();
                handleEdit(original);
              }}
              title="Edit"
              size="large"
            >
              <Edit />
            </IconButton>
            <IconButton
              className={classes.favoriteButton}
              onClick={e => {
                e.stopPropagation();
                handleFavorite(original);
              }}
              size="large"
              title="Favorite"
            >
              {original.isFavorite ? <Favorite /> : <FavoriteBorder />}
            </IconButton>
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Order Date:
          </FormLabel>
          <Typography component="span" variant="body2">
            {formatDate(dateOrdered)}
          </Typography>
        </div>
        {!isActive && (
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              Order No:
            </FormLabel>
            <Typography component="span" variant="body2">
              {sageOrderNumbers.join(', ') || '--'}
            </Typography>
          </div>
        )}
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Order Type:
          </FormLabel>
          <Typography component="span" variant="body2">
            {type || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Description:
          </FormLabel>
          <Typography component="span" variant="body2">
            {(description && description.name) || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Job:
          </FormLabel>
          <Typography component="span" variant="body2">
            {job || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Vendor:
          </FormLabel>
          {vendors?.map((vendor, index) => {
            return (
              <Typography component="span" key={`${index}`} variant="body2">
                {vendor.vendorName}
                {index !== vendors.length - 1 ? ', ' : ''}
              </Typography>
            );
          })}
          {!vendors || vendors?.length === 0 ? '--' : ''}
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Owner:
          </FormLabel>
          <Typography component="span" variant="body2">
            {orderPlacer || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Total:
          </FormLabel>
          <Typography component="span" variant="body2">
            {totalCost ? formatMoney(totalCost) : '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Status:
          </FormLabel>
          <Typography
            component="span"
            variant="body2"
            className={
              currentStatus && currentStatus.status && currentStatus.status === 'Remediating'
                ? classes.statusRed
                : null
            }
          >
            {currentStatus && currentStatus.status ? currentStatus.status : '--'}
          </Typography>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    '@media (min-width: 600px)': {
      flexDirection: 'row',
      alignItems: 'center'
    },
    '&:first-child': {
      marginTop: 0
    }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  roleWrapper: {
    display: 'flex',
    alignItems: 'center'
  },
  editButton: {
    padding: `2px 5px`
  },
  favoriteButton: {
    padding: `2px 5px`,
    color: theme.palette.grey[400]
  },
  approveButton: {
    padding: `2px 5px`
  },
  boldName: {
    color: theme.palette.typography.labels,
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    fontSize: theme.typography.body2.fontSize
  },
  buttonsWrapper: {
    display: 'flex'
  },
  activeName: {
    color: theme.palette.grey[500]
  },
  divider: {
    margin: '0 5px'
  },
  part: {
    flex: 1,
    fontSize: 14
  },
  statusRed: {
    color: theme.palette.error.main
  },
  tableCheckbox: {
    marginRight: 0
  },
  duplicateIcon: {
    marginRight: `8px`
  },
  undefinedPart: {
    color: theme.palette.error.main
  },
  partItem: {
    display: 'block'
  }
}));
